import React from 'react';
import { STORES } from './data.jsx';
import { OPERATIONAL_NOTIFICATIONS_MOCK } from './extended-mock-data.jsx';
import { Icon } from './icons.jsx';
import { notificationService, tenantService } from './services.jsx';
// Painel de notificações — Super Admin Tezus
// Fonte única: OPERATIONAL_NOTIFICATIONS_MOCK via notificationService
// Não usa buildNotifications — fonte de dados unificada para sino + página

const { useMemo: useMemoNotif, useState: useStateNotif, useEffect: useEffectNotif, useRef: useRefNotif } = React;

// severity → visual
const NOTIF_SEVERITY = {
  critical: { color: 'var(--danger)',  bg: 'var(--danger-bg)',  icon: 'alert',          label: 'Crítico'   },
  warning:  { color: 'var(--warning)', bg: 'var(--warning-bg)', icon: 'alert',          label: 'Atenção'   },
  security: { color: 'var(--danger)',  bg: 'var(--danger-bg)',  icon: 'shield',         label: 'Segurança' },
  info:     { color: 'var(--info)',    bg: 'var(--info-bg)',    icon: 'bell',           label: 'Info'      },
  sla_ticket:{ color: 'var(--danger)', bg: 'var(--danger-bg)',  icon: 'messageCircle',  label: 'SLA'       },
};

// SLA status badge
const SLANotifBadge = ({ sla_status, sla_time, priority }) => {
  const priColor = { urgente: 'var(--danger)', alta: 'var(--warning)', media: 'var(--info)', baixa: 'var(--text-muted)' };
  const priLabel = { urgente: 'Urgente', alta: 'Alta', media: 'Média', baixa: 'Baixa' };
  return (
    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 5 }}>
      {sla_status === 'vencido' && (
        <span style={{ display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:20,background:'var(--danger-bg)',color:'var(--danger)',fontSize:10,fontWeight:700 }}>
          <Icon name="alert" size={9}/>SLA VENCIDO · {sla_time}
        </span>
      )}
      {sla_status === 'vencendo' && (
        <span style={{ display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:20,background:'var(--warning-bg)',color:'var(--warning)',fontSize:10,fontWeight:700 }}>
          <Icon name="clock" size={9}/>VENCENDO · {sla_time}
        </span>
      )}
      {sla_status === 'novo' && (
        <span style={{ display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:20,background:'var(--info-bg)',color:'var(--info)',fontSize:10,fontWeight:700 }}>
          <Icon name="bell" size={9}/>NOVO · {sla_time}
        </span>
      )}
      {sla_status === 'normal' && (
        <span style={{ display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:20,background:'var(--bg-subtle)',color:'var(--text-muted)',fontSize:10,fontWeight:600 }}>
          <Icon name="clock" size={9}/>{sla_time}
        </span>
      )}
      {priority && (
        <span style={{ display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:20,background:'transparent',border:`1px solid ${priColor[priority]||'var(--border)'}`,color:priColor[priority]||'var(--text-muted)',fontSize:10,fontWeight:600 }}>
          {priLabel[priority]||priority}
        </span>
      )}
    </div>
  );
};

// Abre o Tenant Cockpit para um tenant_id
function openCockpitForTenantId(tenantId, onNav, onClose) {
  if (!tenantId) return;
  const tryNavigate = (store) => {
    if (store) {
      onNav({ page: 'cockpit', store });
      onClose();
      return true;
    }
    return false;
  };

  const allStores = STORES || [];
  const localStore = allStores.find(s =>
    String(s.tenant_id) === String(tenantId) ||
    String(s.id) === String(tenantId)
  );
  if (tryNavigate(localStore)) return;

  // Modo real: STORES pode estar vazio; tenta resolver via API.
  if (tenantService?.getById) {
    (async () => {
      try {
        const r = await tenantService.getById(tenantId);
        if (tryNavigate(r?.data)) return;
      } catch (e) {
        // ignore and fallback
      }
      onNav({ page: 'notificacoes-ops' });
      onClose();
    })();
    return;
  }

  onNav({ page: 'notificacoes-ops' });
  onClose();
}

const NotificationsPanel = ({ open, onClose, onNav, onToast }) => {
  // Fonte única: window._notifStore (singleton reativo)
  const store = window._notifStore;
  const [notifs,  setNotifs]  = useStateNotif(() => store ? store.getAll() : []);
  const [filter,  setFilter]  = useStateNotif('all');
  const panelRef = useRefNotif(null);

  // Sincroniza com o store global quando ele muda
  useEffectNotif(() => {
    if (!store) return;
    const unsub = store.subscribe(updated => setNotifs(updated));
    return unsub;
  }, []);

  useEffectNotif(() => {
    if (!open) return;
    const handler = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!open) return null;

  const markRead    = async (id) => {
    try {
      if (store) await store.markRead(id);
      else setNotifs(ns => ns.map(n => n.id === id ? { ...n, _read: true } : n));
    } catch (e) { onToast && onToast(e?.message || 'Erro ao marcar como lida'); }
  };
  const markAllRead = async () => {
    try {
      if (store) await store.markAllRead();
      else setNotifs(ns => ns.map(n => ({ ...n, _read: true })));
    } catch (e) { onToast && onToast(e?.message || 'Erro ao marcar todas como lidas'); }
  };

  const unreadCount = notifs.filter(n => !n._read).length;

  const filtered = useMemoNotif(() => {
    if (filter === 'all')        return notifs;
    if (filter === 'unread')     return notifs.filter(n => !n._read);
    if (filter === 'sla_ticket') return notifs.filter(n => n.type === 'sla_ticket');
    return notifs.filter(n => n.severity === filter);
  }, [notifs, filter]);

  return (
    <div ref={panelRef} style={{
      position: 'fixed', top: 60, right: 12, zIndex: 80,
      width: 440, maxHeight: 'calc(100vh - 80px)',
      background: 'var(--bg-elevated)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-lg)',
      display: 'flex', flexDirection: 'column',
      animation: 'scale-in 160ms cubic-bezier(0.2,0.8,0.2,1)',
      transformOrigin: 'top right',
    }}>
      {/* Head */}
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Notificações operacionais</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
            {unreadCount > 0 ? `${unreadCount} não lida${unreadCount !== 1 ? 's' : ''}` : 'Todas lidas'} · {notifs.length} total
          </div>
        </div>
        {unreadCount > 0 && (
          <button className="btn sm ghost" onClick={markAllRead} style={{ fontSize: 11 }}>Marcar todas lidas</button>
        )}
        <button className="icon-action" onClick={onClose}><Icon name="x" size={15} /></button>
      </div>

      {/* Filtros */}
      <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {[
          { id: 'all',        label: 'Todas' },
          { id: 'unread',     label: unreadCount > 0 ? `Não lidas (${unreadCount})` : 'Não lidas' },
          { id: 'sla_ticket', label: 'SLA / Tickets', icon: 'messageCircle' },
          { id: 'critical',   label: 'Críticas' },
          { id: 'warning',    label: 'Atenção' },
          { id: 'security',   label: 'Segurança' },
        ].map(f => (
          <button key={f.id}
            className={'filter-chip ' + (filter === f.id ? 'active' : '')}
            style={{ height: 26, fontSize: 11 }}
            onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Lista */}
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {filtered.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            <Icon name="check" size={24} style={{ display: 'block', margin: '0 auto 8px', opacity: 0.4 }} />
            Nenhuma notificação {filter === 'unread' ? 'não lida' : 'nesta categoria'}
          </div>
        )}
        {filtered.map(n => {
          const isSLA  = n.type === 'sla_ticket';
          const meta   = isSLA
            ? (n.sla_status === 'vencido' ? NOTIF_SEVERITY.critical : NOTIF_SEVERITY.warning)
            : (NOTIF_SEVERITY[n.severity] || NOTIF_SEVERITY.info);
          const isRead = n._read;
          return (
            <div key={n.id}
              style={{
                padding: '12px 14px', borderBottom: '1px solid var(--border)',
                display: 'flex', gap: 10,
                background: isRead ? 'transparent' : 'color-mix(in srgb, var(--brand-navy) 3%, var(--bg-elevated))',
                cursor: 'pointer', transition: 'background 100ms',
              }}
              onClick={() => markRead(n.id)}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isRead ? 'transparent' : 'color-mix(in srgb, var(--brand-navy) 3%, var(--bg-elevated))'; }}
            >
              <div style={{ width: 34, height: 34, borderRadius: 8, background: meta.bg, color: meta.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={isSLA ? 'messageCircle' : meta.icon} size={15} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: isRead ? 400 : 600, color: 'var(--text)', lineHeight: 1.35, marginBottom: 2 }}>
                  {n.title}
                </div>
                {n.tenant && (
                  <div style={{ fontSize: 11, color: meta.color, fontWeight: 600, marginBottom: 3 }}>{n.tenant}</div>
                )}
                {/* SLA badges */}
                {isSLA && (
                  <SLANotifBadge sla_status={n.sla_status} sla_time={n.sla_time} priority={n.priority} />
                )}
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 7, lineHeight: 1.4 }}>{n.body}</div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Ticket action */}
                  {isSLA && n.ticket_id && (
                    <button
                      className="btn sm"
                      style={{ fontSize: 11, height: 24, padding: '0 8px', background: meta.bg, color: meta.color, border: 'none', fontWeight: 600 }}
                      onClick={e => { e.stopPropagation(); markRead(n.id); onNav({ page: 'tickets', openTicketId: n.ticket_id }); onClose(); }}
                    >
                      <Icon name="messageCircle" size={10} />Abrir Ticket {n.ticket_id}
                    </button>
                  )}
                  {/* Cockpit action (non-SLA with tenant) */}
                  {!isSLA && n.tenant_id && (
                    <button
                      className="btn sm"
                      style={{ fontSize: 11, height: 24, padding: '0 8px', background: meta.bg, color: meta.color, border: 'none', fontWeight: 600 }}
                      onClick={e => { e.stopPropagation(); openCockpitForTenantId(n.tenant_id, onNav, onClose); }}
                    >
                      <Icon name="activity" size={10} />Abrir Cockpit
                    </button>
                  )}
                  {!isRead && (
                    <button
                      className="btn sm ghost"
                      style={{ fontSize: 11, height: 24, padding: '0 8px' }}
                      onClick={e => { e.stopPropagation(); markRead(n.id); }}
                    >
                      Marcar lida
                    </button>
                  )}
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>{n.created_at}</span>
                  {!isRead && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--brand-navy)', flexShrink: 0 }} />}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8 }}>
        <button className="btn sm primary" style={{ flex: 1 }} onClick={() => { onNav({ page: 'notificacoes-ops' }); onClose(); }}>
          <Icon name="bell" size={12} />Ver todas as notificações
        </button>
        {unreadCount > 0 && (
          <button className="btn sm ghost" onClick={markAllRead}>Tudo lido</button>
        )}
      </div>
    </div>
  );
};

// Modal de envio de notificação ao lojista (mantido para outras páginas)
const SendNotifModal = ({ open, notif, onClose, onSend }) => {
  const [channel, setChannel] = useStateNotif('email');
  const [msg,     setMsg]     = useStateNotif('');

  useEffectNotif(() => {
    if (open && notif) {
      setMsg(`Olá! Temos um aviso importante sobre sua loja ${notif.tenant || notif.store?.name || ''}.`);
      setChannel('email');
    }
  }, [open, notif]);

  if (!open || !notif) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <div className="modal-icon" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
            <Icon name="bell" size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <h3>Enviar notificação ao lojista</h3>
            <p className="modal-sub">{notif.tenant || notif.store?.name} · {notif.store?.ownerEmail || 'e-mail do proprietário'}</p>
          </div>
        </div>
        <div className="modal-body" style={{ paddingLeft: 22 }}>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>Canal de envio</div>
            <div className="radio-card-row">
              {[
                { id: 'email',    label: 'E-mail',         desc: 'Entrega garantida'            },
                { id: 'whatsapp', label: 'WhatsApp',       desc: 'Requer telefone cadastrado'   },
                { id: 'push',     label: 'Push no painel', desc: 'Aparece no dashboard da loja' },
              ].map(c => (
                <div key={c.id} className={'radio-card ' + (channel === c.id ? 'selected' : '')} onClick={() => setChannel(c.id)} style={{ flex: 1 }}>
                  <div className="title">{c.label}</div>
                  <div className="desc">{c.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>Mensagem</div>
            <textarea
              value={msg} onChange={e => setMsg(e.target.value)}
              style={{ width: '100%', height: 100, padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border-strong)', background: 'var(--bg-elevated)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', lineHeight: 1.5 }}
            />
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{msg.length} caracteres · registrado na auditoria</div>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn ghost" onClick={onClose}>Cancelar</button>
          <button className="btn primary" onClick={() => onSend(channel, msg)} disabled={!msg.trim()}>
            <Icon name="bell" size={13} />Enviar notificação
          </button>
        </div>
      </div>
    </div>
  );
};

export {
  NOTIF_SEVERITY,
  NotificationsPanel,
  SendNotifModal,
  openCockpitForTenantId,
};
